import React, { useState, useCallback, useEffect } from 'react';
import { useQRScans } from '../../authentication_components/businessScansScontext';
import EditScanModal from './utils/EditScansModal';
import './styles/QRScansManager.css'
import './styles/QRscans.css'

/**
 * QRScansManager Component - WITH EDIT FUNCTIONALITY
 * 
 * A comprehensive component for managing and viewing QR scan data.
 * Users enter a registry number to view scans, statistics, and analytics.
 * Includes ability to edit individual scan records.
 * 
 * Features:
 * - Registry number input validation
 * - Real-time scan data with pagination
 * - Advanced filtering options
 * - Edit individual scans in modal dialog
 * - Statistics and analytics dashboard
 * - Data export to CSV
 * - Responsive UI with loading/error states
 */
export function QRScansManager() {
    // ========================
    // STATE MANAGEMENT
    // ========================

    const [registryNumber, setRegistryNumber] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [selectedRegistry, setSelectedRegistry] = useState(null);
    const [activeTab, setActiveTab] = useState('scans'); // scans, statistics, analytics

    // Scans state
    const [scans, setScans] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [scansPerPage] = useState(20);

    // Filters state
    const [filters, setFilters] = useState({
        country: '',
        deviceType: '',
        browserName: '',
        dateFrom: '',
        dateTo: '',
        hasLocation: null,
        hasDeviceInfo: null,
    });

    // Summary and stats state
    const [summary, setSummary] = useState(null);
    const [stats, setStats] = useState(null);

    // Edit modal state
    const [editingScans, setEditingScans] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingScan, setEditingScan] = useState(null);

    // Context methods
    const {
        getScansByBusinessRegistry,
        getScanSummary,
        getScanStatsByRegistry,
        formatScanData,
        validateScanFilters,
        exportScansData,
        loading,
        error,
        clearError,
    } = useQRScans();

    // ========================
    // LOAD DATA FUNCTIONS
    // ========================

    /**
     * Load scans for the selected registry
     */
    const loadScans = useCallback(async () => {
        if (!selectedRegistry) return;

        try {
            const filterValidation = validateScanFilters(filters);
            if (filterValidation.length > 0) {
                console.warn('Filter validation errors:', filterValidation);
                return;
            }

            const options = {
                limit: scansPerPage,
                offset: currentPage * scansPerPage,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([, value]) => value !== '' && value !== null)
                ),
            };

            const result = await getScansByBusinessRegistry(selectedRegistry, options);

            const formattedScans = result.scans.map(formatScanData);
            setScans(formattedScans);
            setTotalCount(result.total_count);
        } catch (err) {
            console.error('Error loading scans:', err);
        }
    }, [selectedRegistry, currentPage, filters, scansPerPage, getScansByBusinessRegistry, formatScanData, validateScanFilters]);

    /**
     * Load summary and statistics
     */
    const loadSummaryAndStats = useCallback(async () => {
        if (!selectedRegistry) return;

        try {
            const [summaryData, statsData] = await Promise.all([
                getScanSummary(selectedRegistry),
                getScanStatsByRegistry(selectedRegistry),
            ]);

            setSummary(summaryData);
            setStats(statsData);
        } catch (err) {
            console.error('Error loading summary:', err);
        }
    }, [selectedRegistry, getScanSummary, getScanStatsByRegistry]);

    /**
     * Handle registry search
     */
    const handleSearchRegistry = useCallback(async (e) => {
        e.preventDefault();

        if (!registryNumber.trim()) {
            console.warn('Registry number cannot be empty');
            return;
        }

        setIsSearching(true);
        setSelectedRegistry(registryNumber.trim());
        setCurrentPage(0);
        setFilters({
            country: '',
            deviceType: '',
            browserName: '',
            dateFrom: '',
            dateTo: '',
            hasLocation: null,
            hasDeviceInfo: null,
        });

        setIsSearching(false);
    }, [registryNumber]);

    /**
     * Handle filter change
     */
    const handleFilterChange = useCallback((filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value === '' ? '' : value,
        }));
        setCurrentPage(0); // Reset to first page when filtering
    }, []);

    /**
     * Clear all filters
     */
    const clearFilters = useCallback(() => {
        setFilters({
            country: '',
            deviceType: '',
            browserName: '',
            dateFrom: '',
            dateTo: '',
            hasLocation: null,
            hasDeviceInfo: null,
        });
        setCurrentPage(0);
    }, []);

    /**
     * Handle export data
     */
    const handleExport = useCallback(async () => {
        if (!selectedRegistry) return;

        try {
            const exportResult = await exportScansData(selectedRegistry, filters);

            // Convert to CSV
            if (exportResult.data.length === 0) {
                console.warn('No data to export');
                return;
            }

            const headers = Object.keys(exportResult.data[0]);
            const csvContent = [
                headers.join(','),
                ...exportResult.data.map(row =>
                    headers.map(header => {
                        const value = row[header];
                        // Escape commas and quotes in values
                        return typeof value === 'string' && value.includes(',')
                            ? `"${value.replace(/"/g, '""')}"`
                            : value;
                    }).join(',')
                ),
            ].join('\n');

            // Download CSV
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', exportResult.filename);
            link.style.visibility = 'hidden';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            console.log(`Downloaded: ${exportResult.filename}`);
        } catch (err) {
            console.error('Error exporting data:', err);
        }
    }, [selectedRegistry, filters, exportScansData]);

    /**
     * Open edit modal for a scan
     */
    const handleEditScan = useCallback((scan) => {
        setEditingScan(scan);
        setEditModalOpen(true);
    }, []);

    /**
     * Handle successful scan update
     */
    const handleScanUpdateSuccess = useCallback((updatedScan) => {
        // Update the scan in the list
        setScans(prevScans =>
            prevScans.map(s => s.scan_id === updatedScan.scan_id ? updatedScan : s)
        );

        // Reload summary and stats since they might be affected
        loadSummaryAndStats();

        setEditModalOpen(false);
        setEditingScan(null);
    }, [loadSummaryAndStats]);

    /**
     * Close edit modal
     */
    const handleCloseEditModal = useCallback(() => {
        setEditModalOpen(false);
        setEditingScan(null);
    }, []);

    // ========================
    // EFFECTS
    // ========================

    // Load scans when registry or filters change
    useEffect(() => {
        if (selectedRegistry) {
            loadScans();
        }
    }, [selectedRegistry, currentPage, filters, loadScans]);

    // Load summary and stats when registry changes
    useEffect(() => {
        if (selectedRegistry) {
            loadSummaryAndStats();
        }
    }, [selectedRegistry, loadSummaryAndStats]);

    // ========================
    // RENDER FUNCTIONS
    // ========================

    const renderRegistryInput = () => (
        <div className="qr-registry-input-section">
            <h2>QR Scans Manager</h2>
            <form onSubmit={handleSearchRegistry} className="registry-form">
                <div className="form-group">
                    <label htmlFor="registry-input">Business Registry Number</label>
                    <input
                        id="registry-input"
                        type="text"
                        placeholder="Enter registry number (e.g., REG123456)"
                        value={registryNumber}
                        onChange={(e) => setRegistryNumber(e.target.value)}
                        className="registry-input"
                        disabled={isSearching}
                    />
                    <button
                        type="submit"
                        className="search-button"
                        disabled={isSearching || !registryNumber.trim()}
                    >
                        {isSearching ? 'Searching...' : 'Search'}
                    </button>
                </div>
            </form>

            {selectedRegistry && (
                <div className="selected-registry-info">
                    <p>
                        Viewing data for: <strong>{selectedRegistry}</strong>
                    </p>
                </div>
            )}
        </div>
    );

    const renderStatisticsCards = () => {
        if (!summary) return null;

        return (
            <div className="statistics-cards">
                <div className="stat-card">
                    <h4>Total Scans</h4>
                    <p className="stat-value">{summary.totalScans.toLocaleString()}</p>
                    <p className="stat-label">All time</p>
                </div>

                <div className="stat-card">
                    <h4>Unique Countries</h4>
                    <p className="stat-value">{summary.uniqueCountries}</p>
                    <p className="stat-label">Geographic reach</p>
                </div>

                <div className="stat-card">
                    <h4>This Month</h4>
                    <p className="stat-value">{summary.scansThisMonth.toLocaleString()}</p>
                    <p className="stat-label">Current month</p>
                </div>

                <div className="stat-card">
                    <h4>Growth</h4>
                    <p className="stat-value">{summary.monthOverMonthGrowth}</p>
                    <p className="stat-label">Month over month</p>
                </div>

                <div className="stat-card">
                    <h4>Last 30 Days</h4>
                    <p className="stat-value">{summary.scansLastMonth.toLocaleString()}</p>
                    <p className="stat-label">Previous month</p>
                </div>

                <div className="stat-card">
                    <h4>Avg Daily Scans</h4>
                    <p className="stat-value">{parseFloat(summary.averageScansPerDay).toFixed(1)}</p>
                    <p className="stat-label">Daily average</p>
                </div>
            </div>
        );
    };

    const renderTopData = () => {
        if (!summary) return null;

        return (
            <div className="top-data-grid">
                <div className="top-data-section">
                    <h4>Top Countries</h4>
                    {summary.topCountries.length > 0 ? (
                        <ul className="top-data-list">
                            {summary.topCountries.map(({ country, count }, idx) => (
                                <li key={country} className="top-data-item">
                                    <span className="rank">{idx + 1}</span>
                                    <span className="name">{country}</span>
                                    <span className="count">{count.toLocaleString()}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="no-data">No data available</p>
                    )}
                </div>

                <div className="top-data-section">
                    <h4>Top Devices</h4>
                    {summary.topDevices.length > 0 ? (
                        <ul className="top-data-list">
                            {summary.topDevices.map(({ device, count }, idx) => (
                                <li key={device} className="top-data-item">
                                    <span className="rank">{idx + 1}</span>
                                    <span className="name">{device || 'Unknown'}</span>
                                    <span className="count">{count.toLocaleString()}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="no-data">No data available</p>
                    )}
                </div>

                <div className="top-data-section">
                    <h4>Top Browsers</h4>
                    {summary.topBrowsers.length > 0 ? (
                        <ul className="top-data-list">
                            {summary.topBrowsers.map(({ browser, count }, idx) => (
                                <li key={browser} className="top-data-item">
                                    <span className="rank">{idx + 1}</span>
                                    <span className="name">{browser || 'Unknown'}</span>
                                    <span className="count">{count.toLocaleString()}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="no-data">No data available</p>
                    )}
                </div>
            </div>
        );
    };

    const renderFilters = () => (
        <div className="filters-section">
            <h3>Filters</h3>
            <div className="filters-grid">
                <div className="filter-group">
                    <label htmlFor="filter-country">Country</label>
                    <input
                        id="filter-country"
                        type="text"
                        placeholder="Filter by country"
                        value={filters.country}
                        onChange={(e) => handleFilterChange('country', e.target.value)}
                        className="filter-input"
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-device">Device Type</label>
                    <select
                        id="filter-device"
                        value={filters.deviceType}
                        onChange={(e) => handleFilterChange('deviceType', e.target.value)}
                        className="filter-select"
                    >
                        <option value="">All Devices</option>
                        <option value="desktop">Desktop</option>
                        <option value="mobile">Mobile</option>
                        <option value="tablet">Tablet</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-browser">Browser</label>
                    <input
                        id="filter-browser"
                        type="text"
                        placeholder="Filter by browser"
                        value={filters.browserName}
                        onChange={(e) => handleFilterChange('browserName', e.target.value)}
                        className="filter-input"
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-date-from">Date From</label>
                    <input
                        id="filter-date-from"
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className="filter-input"
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-date-to">Date To</label>
                    <input
                        id="filter-date-to"
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className="filter-input"
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-location">Has Location</label>
                    <select
                        id="filter-location"
                        value={filters.hasLocation === null ? '' : filters.hasLocation}
                        onChange={(e) => handleFilterChange('hasLocation', e.target.value === '' ? null : e.target.value === 'true')}
                        className="filter-select"
                    >
                        <option value="">Any</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </div>
            </div>

            <div className="filter-actions">
                <button onClick={clearFilters} className="clear-filters-button">
                    Clear All Filters
                </button>
            </div>
        </div>
    );

    const renderScansTable = () => {
        if (scans.length === 0) {
            return <p className="no-scans">No scans found. Try adjusting your filters.</p>;
        }

        const totalPages = Math.ceil(totalCount / scansPerPage);

        return (
            <div className="scans-table-section">
                <div className="table-header">
                    <h3>Recent Scans ({totalCount} total)</h3>
                    <button onClick={handleExport} className="export-button">
                        📥 Export to CSV
                    </button>
                </div>

                <div className="table-responsive">
                    <table className="scans-table">
                        <thead>
                            <tr>
                                <th>Scan ID</th>
                                <th>Date & Time</th>
                                <th>Location</th>
                                <th>Device</th>
                                <th>Browser</th>
                                <th>OS</th>
                                <th>IP Address</th>
                                <th>Referrer</th>
                                <th>User Agent</th>
                                <th>QR Name</th>
                                <th>Time Ago</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {scans.map(scan => (
                                <tr key={scan.scan_id} className="scan-row">
                                    <td className="scan-id">#{scan.scan_id}</td>
                                    <td className="scan-datetime">{scan.formattedDateTime}</td>
                                    <td className="scan-location">
                                        <span className={`location-badge ${scan.hasLocation ? 'with-location' : 'no-location'}`}>
                                            {scan.locationText}
                                        </span>
                                    </td>
                                    <td className="scan-device">
                                        <span className="device-badge">{scan.deviceText}</span>
                                    </td>
                                    <td className="scan-browser">{scan.browser_name || 'Unknown'}</td>
                                    <td className="scan-os">{scan.os_name || 'Unknown'}</td>
                                    <td className="scan-ip">{scan.ip_address || 'N/A'}</td>
                                    <td className="scan-referrer" title={scan.referrer || ''}>
                                        {scan.referrer || 'Direct'}
                                    </td>
                                    <td className="scan-user-agent" title={scan.user_agent || ''}>
                                        {scan.user_agent || 'N/A'}
                                    </td>
                                    <td className="scan-qr-name">{scan.qr_name || 'N/A'}</td>
                                    <td className="scan-time-ago">
                                        <span className="time-ago-badge">{scan.timeAgo}</span>
                                    </td>
                                    <td className="scan-actions">
                                        <button
                                            onClick={() => handleEditScan(scan)}
                                            className="edit-scan-button"
                                            title="Edit this scan"
                                        >
                                            ✎ Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination — unchanged */}
                <div className="pagination">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                        disabled={currentPage === 0}
                        className="pagination-button"
                    >
                        ← Previous
                    </button>
                    <span className="pagination-info">
                        Page {currentPage + 1} of {totalPages > 0 ? totalPages : 1}
                    </span>
                    <button
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={currentPage >= totalPages - 1}
                        className="pagination-button"
                    >
                        Next →
                    </button>
                </div>
            </div>
        );
    };

    const renderLoadingState = () => (
        <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading QR scan data...</p>
        </div>
    );

    const renderErrorState = () => (
        <div className="error-container">
            <h3>⚠️ Error</h3>
            <p>{error}</p>
            <button onClick={clearError} className="dismiss-button">
                Dismiss
            </button>
        </div>
    );

    // ========================
    // MAIN RENDER
    // ========================

    return (
        <div className="qr-scans-manager">
            {/* Header */}
            {renderRegistryInput()}

            {/* Error State */}
            {error && renderErrorState()}

            {/* Loading State */}
            {loading && renderLoadingState()}

            {/* Main Content */}
            {selectedRegistry && !loading && (
                <div className="manager-content">
                    {/* Tab Navigation */}
                    <div className="tab-navigation">
                        <button
                            className={`tab-button ${activeTab === 'scans' ? 'active' : ''}`}
                            onClick={() => setActiveTab('scans')}
                        >
                            📊 Scans
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'statistics' ? 'active' : ''}`}
                            onClick={() => setActiveTab('statistics')}
                        >
                            📈 Statistics
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
                            onClick={() => setActiveTab('analytics')}
                        >
                            🎯 Analytics
                        </button>
                    </div>

                    {/* Scans Tab */}
                    {activeTab === 'scans' && (
                        <div className="tab-content scans-tab">
                            {renderFilters()}
                            {renderScansTable()}
                        </div>
                    )}

                    {/* Statistics Tab */}
                    {activeTab === 'statistics' && (
                        <div className="tab-content statistics-tab">
                            {renderStatisticsCards()}
                        </div>
                    )}

                    {/* Analytics Tab */}
                    {activeTab === 'analytics' && (
                        <div className="tab-content analytics-tab">
                            <div className="analytics-section">
                                <h3>Top Insights</h3>
                                {renderTopData()}
                            </div>

                            {summary && (
                                <div className="summary-details">
                                    <h3>Additional Metrics</h3>
                                    <div className="metrics-grid">
                                        <div className="metric">
                                            <label>Unique Cities</label>
                                            <p className="metric-value">{summary.uniqueCities}</p>
                                        </div>
                                        <div className="metric">
                                            <label>Unique Browsers</label>
                                            <p className="metric-value">{summary.uniqueBrowsers}</p>
                                        </div>
                                        <div className="metric">
                                            <label>First Scan</label>
                                            <p className="metric-value">
                                                {summary.firstScanTime
                                                    ? new Date(summary.firstScanTime).toLocaleDateString()
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="metric">
                                            <label>Last Scan</label>
                                            <p className="metric-value">
                                                {summary.lastScanTime
                                                    ? new Date(summary.lastScanTime).toLocaleDateString()
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Initial State Message */}
            {!selectedRegistry && !loading && (
                <div className="initial-state">
                    <p>Enter a business registry number to view QR scan data.</p>
                </div>
            )}

            {/* Edit Modal */}
            <EditScanModal
                scan={editingScan}
                isOpen={editModalOpen}
                onClose={handleCloseEditModal}
                onSuccess={handleScanUpdateSuccess}
            />
        </div>
    );
}

export default QRScansManager;